import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getOrgComplianceAPI,
  getTermsRepositoryAPI,
  uploadPhysicalMsaAPI,
  createNewTermsVersionAPI,
  updateTermsVersionAPI,
  publishTermsVersionAPI,
  deleteTermsVersionAPI,
  duplicateTermsVersionAPI,
  deactivateTermsVersionAPI,
  getTermsStatusAPI,
  acceptTermsAPI,
  declineTermsAPI,
  getTermsHistoryAPI,
  exportTermsHistoryAPI,
  createOrgProfileAPI,
  uploadMsaDomainSpecificAPI,
  getMsaDomainsAPI,
} from "../../../../services/terms";
import { getAllDomainsAPI } from "../../../../services/userManagement";
import { termsKeys } from "../queryKeys/termsKeys";

// ─── Queries ────────────────────────────────────────────────────────────────

export const useOrgComplianceList = (filters?: {
  search?: string;
  contract_type?: string;
  terms_status?: string;
  sort_by?: string;
  current?: number;
  limit_to?: number;
}) => {
  return useQuery({
    queryKey: termsKeys.orgList(filters),
    queryFn: async () => {
      const response = await getOrgComplianceAPI(filters ?? {});
      if (response?.status === 200) {
        return response.data.data;
      }
      throw new Error("Failed to fetch organization compliance");
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    select: (data) => ({
      organizations: (data.profiles ?? []).map((p: any) => ({
        ...p,
        id: p.id ?? p.organization,
      })),
  total: data.total_count ?? data.total ?? data.profiles?.length ?? 0,
    }),
  });
};

export const useTermsRepositoryList = (filters?: any) => {
  return useQuery({
    queryKey: termsKeys.termsList(filters),
    queryFn: async () => {
      const response = await getTermsRepositoryAPI(filters);
      if (response?.status === 200) {
        return response.data.data;
      }
      throw new Error("Failed to fetch terms repository");
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    select: (data) => {
      // API now filters server-side; just normalise the shape
      const versions = data.versions ?? [];
      return {
        terms: versions,
        total: data.total_count ?? versions.length, // prefer server total for pagination
      };
    },
  });
};

export const useTermsStatus = () => {
  return useQuery({
    queryKey: termsKeys.termsStatus(),
    queryFn: async () => {
      const response = await getTermsStatusAPI();
      if (response?.status === 200) {
        return response.data.data;
      }
      throw new Error("Failed to fetch terms status");
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useTermsHistory = (filters?: any) => {
  return useQuery({
    queryKey: termsKeys.termsHistory(filters),
    queryFn: async () => {
      const response = await getTermsHistoryAPI(filters);
      if (response?.status === 200) {
        return response.data.data;
      }
      throw new Error("Failed to fetch terms history");
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    select: (data) => ({
      events: data.events ?? [],
      total: data.total_count ?? 0,
    }),
  });
};

// ─── Mutations ──────────────────────────────────────────────────────────────

export const useUploadPhysicalMsa = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      organization: string;
      document_file: File;
      version: string;
      effective_date: string;
      title: string;
      notes?: string;
    }) => {
      const response = await uploadMsaDomainSpecificAPI(payload);
      if (response?.status === 200 || response?.status === 201) return response.data;
      throw new Error("Failed to upload MSA");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
    },
  });
};

export const useCreateOrgProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      organization: string;
      legal_entity_name: string;
      contract_type?: "clickwrap" | "signed_msa";
    }) => {
      const response = await createOrgProfileAPI(payload);
      if (response?.status === 200) return response.data;
      throw new Error("Failed to save organization profile");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
    },
  });
};

export const useCreateNewTermsVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      file: File;
      version: string;
      effectiveDate: string;
      title: string;
      scenario: string;
      changeType?: "Material" | "Minor";
      requiresReacceptance?: boolean;
      notes?: string;
    }) => {
      const response = await createNewTermsVersionAPI(payload);
      if (response?.status === 200) return response.data;
      throw new Error("Failed to create terms version");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
    },
  });
};

export const useUpdateTermsVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string | number;
      title?: string;
      effectiveDate?: string;
      scenario?: string;
      changeType?: "Material" | "Minor";
      requiresReacceptance?: boolean;
      notes?: string;
      file?: File;
    }) => {
      const response = await updateTermsVersionAPI(payload);
      if (response?.status === 200) return response.data;
      throw new Error("Failed to update terms version");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
    },
  });
};

export const usePublishTermsVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // API: POST /versions/activate/ — activates selected, deactivates all others
    mutationFn: async (id: string | number) => {
      const response = await publishTermsVersionAPI(id);
      if (response?.status === 200) return response.data;
      throw new Error("Failed to publish terms version");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
    },
  });
};

export const useDeactivateTermsVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | number) => {
      const response = await deactivateTermsVersionAPI(id);
      if (response?.status === 200) return response.data;
      throw new Error("Failed to deactivate terms version");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
    },
  });
};

export const useDeleteTermsVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | number) => {
      const response = await deleteTermsVersionAPI(id);
      if (response?.status === 200) return response.data;
      throw new Error("Failed to delete terms version");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
    },
  });
};

export const useDuplicateTermsVersion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string | number;
      version: string;
      effectiveDate: string;
      title: string;
      scenario: string;
      changeType?: "Material" | "Minor";
      notes?: string;
    }) => {
      const response = await duplicateTermsVersionAPI(payload);
      if (response?.status === 200) return response.data;
      throw new Error("Failed to duplicate terms version");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.lists() });
    },
  });
};

export const useAcceptTerms = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      terms_version: string;
      button_text: string;
      checkbox_text?: string;
      acceptance_method?: "web_login" | "sso_login" | "activation";
      sso_provider?: string;
    }) => {
      const response = await acceptTermsAPI(payload);
      if (response?.status === 200) return response.data;
      throw new Error("Failed to accept terms");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: termsKeys.all });
    },
  });
};

export const useDeclineTerms = () => {
  return useMutation({
    mutationFn: async (payload?: {
      terms_version?: string;
      button_text?: string;
      acceptance_method?: string;
    }) => {
      const response = await declineTermsAPI(payload ?? {});
      if (response?.status === 200) return response.data;
      throw new Error("Failed to decline terms");
    },
    // No cache invalidation — token is revoked on decline, user is logged out
  });
};

export const useExportTermsHistory = () => {
  return useMutation({
    mutationFn: async (organization?: string) => {
      const response = await exportTermsHistoryAPI(organization);
      if (response?.status === 200) return response.data.data;
      throw new Error("Failed to export terms history");
    },
  });
};

export const useMsaDomains = () => {
  return useQuery({
    queryKey: ["msaDomains"], // Add this to your termsKeys if you have a centralized key manager
    queryFn: async () => {
      const response = await getMsaDomainsAPI();
      if (response?.status === 200) {
        return response.data.data;
      }
      throw new Error("Failed to fetch MSA domains");
    },
    staleTime: 1* 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useAllDomains = () => {
  return useQuery({
    queryKey: ["allDomains"],
    queryFn: async () => {
      const response = await getAllDomainsAPI();
      if (response?.status === 200) return response.data.data;
      throw new Error("Failed to fetch domains");
    },
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};